import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { RadioButton } from "primereact/radiobutton";
import { SelectButton } from "primereact/selectbutton";
import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  IBenefitLineSelection,
  IBenefitLineSelector,
  IVocabularyRow,
} from "types/BenefitSelection/BenefitSelection";

import {
  BENEFIT_LABELS,
  COVERAGE_LEVEL_OPTIONS,
  PERIOD_OPTIONS,
  PLACE_OF_SERVICE_OPTIONS,
  tieBreakOptions,
} from "./constants";
import {
  definitionsOfLineType,
  describeLineTypeDefinition,
  describeSelector,
} from "./describeSelector";
import { KeywordChips, withPending } from "./KeywordChips";
import {
  DEFAULT_FALLBACK,
  fallbackOf,
  LineFallback,
  normaliseSelector,
  reorder,
  selectorStatesSomething,
  selectorsWithoutFallback,
  withFallback,
} from "./mapper";
import { Surface } from "./Surface";

const SelectorCard = styled.div<{ $invalid: boolean }>`
  border: 1px solid
    ${({ $invalid }) =>
      $invalid ? "var(--red-400, #f87171)" : "var(--surface-200)"};
  border-radius: 6px;
  padding: 0.75rem;
`;

/** "This account's own service line", "no place of service", or a list of pVerify's labels. */
const PLACE_MODES = [
  { name: "Any place of service", value: "any" },
  { name: "Our service line's", value: "own" },
  { name: "Blank (the line names none)", value: "blank" },
  { name: "One of these", value: "list" },
];

const placeModeOf = (selector: IBenefitLineSelector): string => {
  if (selector.placeOfService === undefined) return "any";
  if (selector.placeOfService === "OWN") return "own";
  if (selector.placeOfService === null) return "blank";
  return "list";
};

/** The criteria keys, i.e. everything a "Lines matching" selector may state. */
const CRITERIA_KEYS: Array<keyof IBenefitLineSelector> = [
  "placeOfService",
  "timePeriodQualifier",
  "coverageLevel",
  "matchKeywords",
  "excludeKeywords",
];

const dropKeys = (
  selector: IBenefitLineSelector,
  keys: Array<keyof IBenefitLineSelector>
): IBenefitLineSelector => {
  const next = { ...selector };
  keys.forEach((key) => delete next[key]);
  return next;
};

interface ILineSelectionDialogProps {
  visible: boolean;
  benefit: string;
  /** The service line this benefit is read from, for the dialog's opening sentence. */
  serviceLineLabel: string;
  /** The saved list, or null when the account has none and reads the built-in default. */
  value: IBenefitLineSelection | null;
  /** What the built-in default is, so "Reset to default" has something honest to reset to. */
  defaultSelection: IBenefitLineSelection;
  /** Every line type any definition names, for the dropdown. */
  knownRoles: string[];
  /** EVERY line type definition, so a chosen type's words can be shown without leaving. */
  vocabulary: IVocabularyRow[];
  onSave: (selection: IBenefitLineSelection | null) => void;
  onHide: () => void;
}

/**
 * CLE-2792 — the ordered list of selectors that decides WHICH LINE inside a service line answers
 * one benefit.
 *
 * 🚨 A SELECTOR IS ONE OF TWO THINGS AND THE EDITOR MAKES YOU SAY WHICH. Either it picks LINES OF A
 * TYPE — a named thing with definitions other people maintain, which is the reusable answer — or it
 * picks LINES MATCHING criteria typed here, which are private to this account and this benefit.
 * Offering both at once let a selector name a type AND narrow it, which reads as one rule and
 * behaves as two, and quietly duplicated definitions into accounts where nobody would find them.
 *
 * 🚨 WHAT HAPPENS WHEN NOTHING MATCHES IS A SETTING, NOT A SELECTOR. It used to be an entry called
 * the catch-all that you added or removed; it is now one radio at the bottom, because the two
 * outcomes — any line will do, versus quote nothing — are a different number in front of a patient
 * and deserve to be asked as a question.
 *
 * 🚨 BLANK IS THE DEFAULT AND SAVING REPLACES IT WHOLE. A benefit with no saved list uses the
 * built-in one; a saved list is never merged with it. "Reset to default" saves `null`, the value
 * that means "use the built-in one" — not a copy of today's default frozen into the row.
 */
export function LineSelectionDialog({
  visible,
  benefit,
  serviceLineLabel: lineLabel,
  value,
  defaultSelection,
  knownRoles,
  vocabulary,
  onSave,
  onHide,
}: ILineSelectionDialogProps) {
  const [draft, setDraft] = useState<IBenefitLineSelection>(
    value ?? defaultSelection
  );
  const [fallback, setFallback] = useState<LineFallback>(
    DEFAULT_FALLBACK[benefit] ?? "ANY_LINE"
  );
  /**
   * ⚠️ RED ONLY AFTER SOMEBODY TRIED TO SAVE. A selector added by pressing "Add selector" is empty
   * by definition; opening it already outlined in red, above an error, marks the editor's own next
   * step as a mistake — and teaches them that red on this screen means nothing.
   */
  const [attempted, setAttempted] = useState(false);
  /** Keyword text typed and not yet a chip, keyed `<index>:<field>` — see `KeywordChips`. */
  const [pending, setPending] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!visible) return;
    const source = value ?? defaultSelection;
    if (!source) {
      setDraft({ selectors: [], tieBreak: "HIGHEST" });
      setFallback(DEFAULT_FALLBACK[benefit] ?? "ANY_LINE");
      setAttempted(false);
      setPending({});
      return;
    }
    setDraft({ ...source, selectors: selectorsWithoutFallback(source) });
    setFallback(fallbackOf(source));
    setAttempted(false);
    setPending({});
  }, [visible, value, defaultSelection, benefit]);

  const setSelector = (index: number, next: IBenefitLineSelector) =>
    setDraft((current) => ({
      ...current,
      selectors: current.selectors.map((selector, i) =>
        i === index ? next : selector
      ),
    }));

  const patchSelector = (index: number, patch: Partial<IBenefitLineSelector>) =>
    setDraft((current) => ({
      ...current,
      selectors: current.selectors.map((selector, i) =>
        i === index ? { ...selector, ...patch } : selector
      ),
    }));

  const dropKey = (index: number, key: keyof IBenefitLineSelector) =>
    setDraft((current) => ({
      ...current,
      selectors: current.selectors.map((selector, i) =>
        i === index ? dropKeys(selector, [key]) : selector
      ),
    }));

  const addSelector = () =>
    setDraft((current) => ({
      ...current,
      selectors: [...current.selectors, {}],
    }));

  const removeSelector = (index: number) =>
    setDraft((current) => ({
      ...current,
      selectors: current.selectors.filter((_, i) => i !== index),
    }));

  const moveSelector = (from: number, to: number) =>
    setDraft((current) => ({
      ...current,
      selectors: reorder(current.selectors, from, to),
    }));

  /**
   * 🚨 EVERY READER OF A SELECTOR GOES THROUGH HERE. The sentence at the top of a card, the
   * validation, and the body of the save all have to see the same selector — including keyword text
   * that has been typed and not yet turned into a chip. Reading `draft` directly is what made typed
   * wording invisible to all three at once.
   */
  const committed = (index: number): IBenefitLineSelector => {
    const selector = draft.selectors[index];
    const match = withPending(
      selector.matchKeywords,
      pending[`${index}:matchKeywords`]
    );
    const exclude = withPending(
      selector.excludeKeywords,
      pending[`${index}:excludeKeywords`]
    );
    return normaliseSelector({
      ...selector,
      matchKeywords: match,
      excludeKeywords: exclude,
    });
  };

  const committedSelectors = draft.selectors.map((_, index) =>
    committed(index)
  );

  const invalidIndexes = committedSelectors
    .map((selector, index) => (selectorStatesSomething(selector) ? -1 : index))
    .filter((index) => index >= 0);

  // Nothing to match on AND nothing to fall back to is a benefit that can never be answered.
  const answersNothing =
    draft.selectors.length === 0 && fallback === "NO_VALUE";

  const handleSave = () => {
    setAttempted(true);
    if (invalidIndexes.length > 0 || answersNothing) return;
    onSave(withFallback({ ...draft, selectors: committedSelectors }, fallback));
  };

  // 🚨 THE RAW VALUE IS THE NAME. A friendly-cased copy beside it ("Physician line
  // (PHYSICIAN_LINE)") reads as two things, and the one people then say out loud is the one that
  // matches nothing when they go looking for it in a selector or a definition.
  const typeOptions = knownRoles.map((role) => ({ label: role, value: role }));

  return (
    <Dialog
      header={`Which line answers the ${(
        BENEFIT_LABELS[benefit] ?? benefit
      ).toLowerCase()}`}
      visible={visible}
      onHide={onHide}
      style={{ width: "min(52rem, 95vw)" }}
      data-testid="benefitSelection.lineSelectionDialog"
      footer={
        <div className="flex justify-content-between gap-2">
          <Button
            type="button"
            label="Reset to default"
            className="p-button-text p-button-sm"
            data-testid="benefitSelection.lineSelectionDialog.reset"
            onClick={() => onSave(null)}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              label="Cancel"
              className="p-button-text p-button-sm"
              data-testid="benefitSelection.lineSelectionDialog.cancel"
              onClick={onHide}
            />
            <Button
              className="p-button-sm"
              type="button"
              label="Save"
              /*
                🚨 ENABLED, AND IT REFUSES ON THE ATTEMPT. A disabled Save cannot be tried, so a
                rule that only reddens "after the user tries to save" would never redden at all, and
                the editor would be left with a dead button and no reason. This is the pattern
                review round 1 settled on for the payer-filter dialog, for the same reason.
              */
              data-testid="benefitSelection.lineSelectionDialog.save"
              onClick={handleSave}
            />
          </div>
        </div>
      }
    >
      <Surface className="flex flex-column gap-3">
        <span
          className="text-sm"
          data-testid="benefitSelection.lineSelectionDialog.intro"
        >
          Rules for picking the{" "}
          {(BENEFIT_LABELS[benefit] ?? benefit).toLowerCase()} from the{" "}
          {lineLabel} service line, checked in order.
        </span>

        <span
          className="text-sm text-color-secondary"
          data-testid="benefitSelection.lineSelectionDialog.ordering"
        >
          Selectors are checked in order. The first one that matches at least
          one line is used and the rest are ignored; if that selector matches
          several lines, the rule at the bottom picks among them.
        </span>

        {draft.selectors.map((selector, index) => {
          const stated = committedSelectors[index];
          const invalid = attempted && !selectorStatesSomething(stated);
          const placeMode = placeModeOf(selector);
          const byType = selector.role !== undefined;
          const definitions = selector.role
            ? definitionsOfLineType(selector.role, vocabulary)
            : [];

          return (
            <SelectorCard
              // The list is ordered and its entries have no identity of their own, so the index IS
              // the key; two identical selectors are two entries, not one.
              // eslint-disable-next-line react/no-array-index-key
              key={`selector-${index}`}
              $invalid={invalid}
              data-testid={`benefitSelection.selector.${index}`}
            >
              <div className="flex align-items-start justify-content-between gap-2 mb-2">
                <p
                  className="m-0 text-sm"
                  data-testid={`benefitSelection.selector.${index}.sentence`}
                >
                  {describeSelector(stated)}
                </p>
                <div className="flex gap-1 flex-shrink-0">
                  {/* One selector has no order to change, so the carets are not shown. */}
                  {draft.selectors.length > 1 && (
                    <>
                      <Button
                        type="button"
                        icon="pi pi-chevron-up"
                        className="p-button-text p-button-sm"
                        aria-label={`Move selector ${index + 1} up`}
                        disabled={index === 0}
                        data-testid={`benefitSelection.selector.${index}.up`}
                        onClick={() => moveSelector(index, index - 1)}
                      />
                      <Button
                        type="button"
                        icon="pi pi-chevron-down"
                        className="p-button-text p-button-sm"
                        aria-label={`Move selector ${index + 1} down`}
                        disabled={index === draft.selectors.length - 1}
                        data-testid={`benefitSelection.selector.${index}.down`}
                        onClick={() => moveSelector(index, index + 1)}
                      />
                    </>
                  )}
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm p-button-danger"
                    aria-label={`Remove selector ${index + 1}`}
                    data-testid={`benefitSelection.selector.${index}.remove`}
                    onClick={() => removeSelector(index)}
                  />
                </div>
              </div>

              <div className="flex gap-4 mb-3">
                <span className="flex align-items-center gap-2">
                  <RadioButton
                    inputId={`selector-${index}-byType`}
                    checked={byType}
                    data-testid={`benefitSelection.selector.${index}.byType`}
                    onChange={() =>
                      setSelector(index, {
                        ...dropKeys(selector, CRITERIA_KEYS),
                        role: "",
                      })
                    }
                  />
                  <label
                    htmlFor={`selector-${index}-byType`}
                    className="text-sm"
                  >
                    Lines of type
                  </label>
                </span>
                <span className="flex align-items-center gap-2">
                  <RadioButton
                    inputId={`selector-${index}-byCriteria`}
                    checked={!byType}
                    data-testid={`benefitSelection.selector.${index}.byCriteria`}
                    onChange={() =>
                      setSelector(index, dropKeys(selector, ["role"]))
                    }
                  />
                  <label
                    htmlFor={`selector-${index}-byCriteria`}
                    className="text-sm"
                  >
                    Lines matching
                  </label>
                </span>
              </div>

              {byType ? (
                <div className="flex flex-column gap-2">
                  <Dropdown
                    id={`selector-${index}-role`}
                    data-testid={`benefitSelection.selector.${index}.role`}
                    className="w-full"
                    options={typeOptions}
                    placeholder="Choose a line type"
                    value={selector.role ?? ""}
                    onChange={(e) => patchSelector(index, { role: e.value })}
                  />
                  {definitions.length > 0 && (
                    <div
                      className="flex flex-column gap-1"
                      data-testid={`benefitSelection.selector.${index}.definitions`}
                    >
                      {definitions.map((row) => (
                        <span
                          key={row.id}
                          className="text-xs text-color-secondary"
                        >
                          {describeLineTypeDefinition(row)}
                        </span>
                      ))}
                    </div>
                  )}
                  {selector.role && definitions.length === 0 && (
                    <span
                      className="text-xs text-orange-700"
                      data-testid={`benefitSelection.selector.${index}.noDefinition`}
                    >
                      No payer defines this line type yet, so this selector
                      matches nothing on every check. Define it in Eligibility ›
                      Line types.
                    </span>
                  )}
                </div>
              ) : (
                <div className="formgrid grid">
                  <div className="field col-12 md:col-6">
                    <Label htmlFor={`selector-${index}-place`}>
                      Place of service
                    </Label>
                    <Dropdown
                      id={`selector-${index}-place`}
                      data-testid={`benefitSelection.selector.${index}.placeMode`}
                      className="w-full"
                      optionLabel="name"
                      options={PLACE_MODES}
                      value={placeMode}
                      onChange={(e) => {
                        if (e.value === "any") dropKey(index, "placeOfService");
                        if (e.value === "own")
                          patchSelector(index, { placeOfService: "OWN" });
                        if (e.value === "blank")
                          patchSelector(index, { placeOfService: null });
                        if (e.value === "list")
                          patchSelector(index, { placeOfService: [] });
                      }}
                    />
                    {placeMode === "list" && (
                      <MultiSelect
                        data-testid={`benefitSelection.selector.${index}.placeList`}
                        className="w-full mt-2"
                        display="chip"
                        options={PLACE_OF_SERVICE_OPTIONS.map((option) => ({
                          label: option,
                          value: option,
                        }))}
                        value={
                          Array.isArray(selector.placeOfService)
                            ? selector.placeOfService
                            : []
                        }
                        onChange={(e) =>
                          patchSelector(index, {
                            placeOfService: e.value ?? [],
                          })
                        }
                      />
                    )}
                  </div>

                  <div className="field col-12 md:col-6">
                    <Label htmlFor={`selector-${index}-period`}>Period</Label>
                    <MultiSelect
                      id={`selector-${index}-period`}
                      data-testid={`benefitSelection.selector.${index}.period`}
                      className="w-full"
                      display="chip"
                      options={PERIOD_OPTIONS.map((option) => ({
                        label: option,
                        value: option,
                      }))}
                      value={selector.timePeriodQualifier ?? []}
                      onChange={(e) =>
                        patchSelector(index, {
                          timePeriodQualifier: e.value ?? [],
                        })
                      }
                    />
                  </div>

                  <div className="field col-12 md:col-6">
                    <Label htmlFor={`selector-${index}-coverage`}>
                      Coverage level
                    </Label>
                    <MultiSelect
                      id={`selector-${index}-coverage`}
                      data-testid={`benefitSelection.selector.${index}.coverage`}
                      className="w-full"
                      display="chip"
                      options={COVERAGE_LEVEL_OPTIONS.map((option) => ({
                        label: option,
                        value: option,
                      }))}
                      value={selector.coverageLevel ?? []}
                      onChange={(e) =>
                        patchSelector(index, { coverageLevel: e.value ?? [] })
                      }
                    />
                  </div>

                  <div className="field col-12 md:col-6">
                    <Label htmlFor={`selector-${index}-match`}>Contains</Label>
                    <KeywordChips
                      id={`selector-${index}-match`}
                      testId={`benefitSelection.selector.${index}.matchKeywords`}
                      value={selector.matchKeywords ?? []}
                      onChange={(values, text) => {
                        patchSelector(index, { matchKeywords: values });
                        setPending((current) => ({
                          ...current,
                          [`${index}:matchKeywords`]: text,
                        }));
                      }}
                    />
                  </div>

                  <div className="field col-12 md:col-6">
                    <Label htmlFor={`selector-${index}-exclude`}>
                      Does not contain
                    </Label>
                    <KeywordChips
                      id={`selector-${index}-exclude`}
                      testId={`benefitSelection.selector.${index}.excludeKeywords`}
                      value={selector.excludeKeywords ?? []}
                      onChange={(values, text) => {
                        patchSelector(index, { excludeKeywords: values });
                        setPending((current) => ({
                          ...current,
                          [`${index}:excludeKeywords`]: text,
                        }));
                      }}
                    />
                  </div>
                </div>
              )}

              {invalid && (
                <span
                  className="text-sm text-red-600"
                  data-testid={`benefitSelection.selector.${index}.invalid`}
                >
                  Set at least one criterion for this selector.
                </span>
              )}
            </SelectorCard>
          );
        })}

        <div className="flex justify-content-center">
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add selector"
            className="p-button-outlined p-button-sm"
            data-testid="benefitSelection.lineSelectionDialog.addSelector"
            onClick={addSelector}
          />
        </div>

        <Divider />

        <div className="flex flex-column gap-2">
          <Label htmlFor="benefitSelection.lineSelectionDialog.fallback">
            If no selector matches:
          </Label>
          <div className="flex gap-4">
            <span className="flex align-items-center gap-2">
              <RadioButton
                inputId="lineFallbackAny"
                checked={fallback === "ANY_LINE"}
                data-testid="benefitSelection.lineSelectionDialog.fallbackAny"
                onChange={() => setFallback("ANY_LINE")}
              />
              <label htmlFor="lineFallbackAny" className="text-sm">
                Use any line on the service line
              </label>
            </span>
            <span className="flex align-items-center gap-2">
              <RadioButton
                inputId="lineFallbackNone"
                checked={fallback === "NO_VALUE"}
                data-testid="benefitSelection.lineSelectionDialog.fallbackNone"
                onChange={() => setFallback("NO_VALUE")}
              />
              <label htmlFor="lineFallbackNone" className="text-sm">
                No value
              </label>
            </span>
          </div>
          {attempted && answersNothing && (
            <span
              className="text-sm text-red-600"
              data-testid="benefitSelection.lineSelectionDialog.answersNothing"
            >
              With no selectors and no fallback this benefit is never answered.
              Add a selector, or fall back to any line.
            </span>
          )}
        </div>

        <Divider />

        <div className="flex flex-column gap-2">
          <Label htmlFor="benefitSelection.lineSelectionDialog.tieBreak">
            If several lines match the selectors, use the:
          </Label>
          <SelectButton
            id="benefitSelection.lineSelectionDialog.tieBreak"
            data-testid="benefitSelection.lineSelectionDialog.tieBreak"
            options={tieBreakOptions(benefit)}
            optionLabel="label"
            optionValue="value"
            value={draft.tieBreak}
            onChange={(e) =>
              e.value &&
              setDraft((current) => ({ ...current, tieBreak: e.value }))
            }
          />
        </div>
      </Surface>
    </Dialog>
  );
}
